import { useMinerDetails } from '@/composables/rc/miner-details'
import { retrieveMinerPart } from '@/helpers/rc/miners'
import { PartStatus } from '@/types/rc/miners/PartStatus'
import { PartType, partTypes, type IndividualPart } from '@/types/rc/miners/PartType'
import { computed, type Ref } from 'vue'
import { useFailureTypes } from './failure-types'

/* Notes: 
/* What used to be called 'repair action' is now labeled 'repair charges', 
/* and what's now labeled 'repair actions' is called 'repair items' by the BE.
/* Clearable fields are cleared by posting `null` to BE. As we don't yet have segregated
/* interfaces for mutations and queries, we cast out null when reading these values.
*/

/**
 * Wraps useMinerDetails query with convenience accesors for part data.
 * Accepts both raw values and refs (to support prop-driven updates in standing components)
 */
export function usePartDetails(minerId: Ref<number>, partName: Ref<IndividualPart>) {
  const { minerDetailsQuery } = useMinerDetails(minerId)
  const { isFailureTypeOther, isFailureTypeASICN } = useFailureTypes()

  const { data: minerDetails, isPending } = minerDetailsQuery

  /**
   * Fetches a part (CB, HB0, etc.) from the miner details
   */
  const getPart = (partName: IndividualPart) =>
    minerDetails.value ? retrieveMinerPart(minerDetails.value, partName) : null

  // Part object
  const partDetails = computed(() => getPart(partName.value))
  const partId = computed(() => partDetails.value?.id)
  const partSn = computed(() => partDetails.value?.sn)
  const partPcbId = computed(() => partDetails.value?.pcb_id)

  // Model
  const partModel = computed(() => partDetails.value?.model ?? undefined) // Cast out null
  const partModelVersion = computed(() => partDetails.value?.model_version ?? undefined) // Cast out null

  // Diagnostic
  const initialDiagnostic = computed(() => partDetails.value?.initial_diagnostic ?? undefined) // Cast out null

  // Failure
  const partFailure = computed(() => partDetails.value?.failure)
  const hasFailure = computed(() => !!partFailure.value)
  const failureTypeId = computed(() => partFailure.value?.failure_type.id)
  const failureDescription = computed(() => partFailure.value?.failure_type.description)
  const failureId = computed(() => partFailure.value?.id)
  const failureReason = computed(() => partFailure.value?.comment ?? undefined) // Cast out null
  const asicN = computed(() => (failureReason.value ? Number(failureReason.value) : 1))
  const hasFailureTypeOther = computed(() => failureTypeId.value && isFailureTypeOther(failureTypeId.value))
  const hasFailureTypeAsicn = computed(() => failureTypeId.value && isFailureTypeASICN(failureTypeId.value))

  // Components
  const partComponents = computed(() => partDetails.value?.replaced_components)

  // Repair
  const repairItems = computed(() => partFailure.value?.repair_items.map((ri) => ri.id) ?? [])
  const hasRepairItems = computed(() => repairItems.value.length > 0)
  const replacedComponents = computed(() => partDetails.value?.replaced_components ?? [])
  const hasReplacedComponents = computed(() => replacedComponents.value.length > 0)
  const repairPictures = computed(() => partFailure.value?.pictures ?? [])
  const hasRepairPictures = computed(() => repairPictures.value.length > 0)
  const repairNotes = computed(() => partDetails.value?.repair_notes ?? '')
  const hasRepairNotes = computed(() => repairNotes.value.length > 0)
  const movedToInventory = computed(() => partDetails.value?.moved_to_inventory)

  // Replacement
  const customerId = computed(() => minerDetails.value?.customer.id)
  const isReplacement = computed(() => partDetails.value?.status === PartStatus.Replaced)
  const partReplacement = computed(() =>
    isReplacement.value && partDetails.value && partDetails.value.replacement_spare_part
      ? {
          part_sn: partDetails.value?.sn,
          part_model: partDetails.value?.model,
          part_model_version: partDetails.value?.model_version,
          ...partDetails.value.replacement_spare_part,
        }
      : undefined
  )

  // Charges (what before was 'repair actions')
  const repairCharges = computed(() => partFailure.value?.executed_repair_action ?? undefined) // Cast out null
  const hasRepairCharges = computed(() => !!repairCharges.value)

  // Status
  const partStatus = computed(() => partDetails.value?.status)
  const hasStatus = computed(() => !!partStatus.value)
  const isFailing = computed(() => partStatus.value === PartStatus.Fail)

  // Damages
  const partDamages = computed(() => partDetails.value?.damages)
  const hasDamages = computed(() => !!partDetails.value?.damages.length)

  // Misc
  const partType = computed(() => partTypes[partName.value])
  const isHb = computed(() => partType.value === PartType.Hashboard)
  const isRecurrent = computed(() => partDetails.value?.has_been_in_rc_before)

  return {
    isPending,
    partDetails,
    partId,
    partSn,
    partPcbId,
    // Model
    partModel,
    partModelVersion,
    // Diagnostic
    initialDiagnostic,
    // Failure
    partFailure,
    hasFailure,
    hasFailureTypeOther,
    hasFailureTypeAsicn,
    failureId,
    failureDescription,
    failureTypeId,
    failureReason,
    asicN,
    // Components
    partComponents,
    // Repair
    repairItems,
    hasRepairItems,
    replacedComponents,
    hasReplacedComponents,
    repairPictures,
    hasRepairPictures,
    repairNotes,
    hasRepairNotes,
    movedToInventory,
    // Replacement
    customerId,
    isReplacement,
    partReplacement,
    // Charges
    repairCharges,
    hasRepairCharges,
    // Status
    partStatus,
    hasStatus,
    isFailing,
    // Damages
    partDamages,
    hasDamages,
    // Misc
    partType,
    isHb,
    isRecurrent,
  }
}
